import * as THREE from 'three';
import { CFG } from './config.js';

/** Consecutive throws before a system is taken out of the loop (~2 s at 60 Hz). */
const FAULT_LIMIT = 120;
import { EventBus } from './bus.js';
import { RNG } from './rng.js';
import { clamp } from './mathx.js';

/**
 * Engine owns the renderer, the scene graph root, the camera, the fixed-timestep
 * loop and the system registry. Every gameplay module is a "system" registered
 * here; systems never reach into each other except through the World context.
 */
export class Engine {
  constructor(canvas) {
    this.canvas = canvas;
    this.bus = new EventBus();
    this.rng = new RNG(20240819);

    const renderer = new THREE.WebGLRenderer({
      canvas, antialias: false, alpha: false, stencil: false, depth: true,
      powerPreference: 'high-performance', preserveDrawingBuffer: true,
    });
    renderer.autoClear = true;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.0;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.shadowMap.autoUpdate = true;
    renderer.info.autoReset = false;
    this.renderer = renderer;

    this.scene = new THREE.Scene();
    this.scene.matrixWorldAutoUpdate = true;

    this.camera = new THREE.PerspectiveCamera(CFG.camera.fov, 16 / 9, CFG.camera.near, CFG.camera.far);
    this.camera.position.set(0, 6, 12);
    this.camera.lookAt(0, 1.4, 0);

    /** Render pipeline: object with render(scene,camera,dt) + setSize(w,h,dpr). Falls back to direct render. */
    this.pipeline = null;

    this.systems = [];
    this._byName = new Map();
    this.running = false;
    this.time = {
      elapsed: 0, sim: 0, dt: 0, fixedDt: CFG.fixedDt, alpha: 0,
      frame: 0, tick: 0, scale: 1, unscaledDt: 0, real: 0,
      hitstop: 0, slowmo: 1,
    };
    this.perf = { fps: 60, ms: 16.7, cpuMs: 0, avg: 16.7, samples: new Float32Array(120), si: 0,
                  drawCalls: 0, triangles: 0, programs: 0, geometries: 0, textures: 0 };
    this._acc = 0;
    this._last = 0;
    this._raf = 0;
    this._resizeObs = null;

    /**
     * Fault state. A system that throws is isolated rather than allowed to
     * take the frame with it, and the failure is reported outward instead of
     * only to the console -- see `_fault` and `onFault`.
     */
    this.faults = [];
    this.onFault = null;              // (fault) => void, set by the boot code
    this.contextLost = false;
    this.onContextLost = null;
    this.onContextRestored = null;

    this.size = { w: 1280, h: 720, dpr: 1 };
    this._bindResize();
    this._bindContext();
  }

  /**
   * The GPU can take the context away at any moment -- a laptop sleeping, a
   * driver reset, a backgrounded mobile tab, memory pressure -- and without a
   * handler the canvas simply goes black forever with the simulation still
   * running behind it. three.js re-initialises its own GPU resources on
   * restore; what it cannot do is tell the player, or stop the clock.
   */
  _bindContext() {
    const c = this.canvas;
    if (!c || !c.addEventListener) return;
    this._onCtxLost = (e) => {
      // preventDefault is what makes a restore possible at all. three.js also
      // calls it, but handler order is not ours to rely on.
      e.preventDefault();
      if (this.contextLost) return;
      this.contextLost = true;
      console.warn('[engine] WebGL context lost');
      try { this.onContextLost?.(); } catch (err) { console.warn('[engine] onContextLost threw', err); }
    };
    this._onCtxRestored = () => {
      if (!this.contextLost) return;
      this.contextLost = false;
      // Wall time kept running while the GPU was gone. Without resetting the
      // clock the first frame back sees a multi-second dt, the fixed loop hits
      // its six-step ceiling and the spiral guard throws the accumulator away.
      this._last = performance.now();
      this._acc = 0;
      console.warn('[engine] WebGL context restored');
      try { this.onContextRestored?.(); } catch (err) { console.warn('[engine] onContextRestored threw', err); }
    };
    c.addEventListener('webglcontextlost', this._onCtxLost, false);
    c.addEventListener('webglcontextrestored', this._onCtxRestored, false);
  }

  /**
   * One hook of one system threw.
   *
   * The counters are PER HOOK, not per system, and that is the whole point.
   * A system can implement several hooks -- `ui` has both update() and
   * lateUpdate() -- and a shared counter is reset by whichever hook still
   * works, so a permanently broken update() looks like a first-time failure on
   * every single frame: it reports forever and never reaches the limit. That
   * is the console flood this was written to stop, and it is exactly what a
   * shared counter produced when tools/reliability-check.mjs first drove it.
   */
  _fault(sys, phase, key, offKey, err) {
    const n = (sys[key] = (sys[key] || 0) + 1);
    if (n === 1) {
      console.error(`[engine] ${sys.name}.${phase} threw`, err);
      const f = { name: sys.name, phase, message: String(err?.message || err), disabled: false };
      this.faults.push(f);
      try { this.onFault?.(f); } catch (e2) { console.warn('[engine] onFault threw', e2); }
    }
    if (n < FAULT_LIMIT || sys[offKey]) return;
    // Drop only the failing hook. The rest of the system may be fine, and
    // taking a working lateUpdate() down with a broken update() would lose
    // more than it saves.
    sys[offKey] = true;
    this._refreshLists();
    console.error(`[engine] ${sys.name}.${phase} disabled after ${n} consecutive throws`);
    const f = { name: sys.name, phase, message: String(err?.message || err), disabled: true };
    this.faults.push(f);
    try { this.onFault?.(f); } catch (e2) { console.warn('[engine] onFault threw', e2); }
  }

  /** Rebuild the dispatch lists, skipping anything disabled by `_fault`. */
  _refreshLists() {
    const S = this.systems;
    this._sysFixed = S.filter(s => s.fixedUpdate && !s._offF);
    this._sysUpdate = S.filter(s => s.update && !s._offU);
    this._sysLate = S.filter(s => s.lateUpdate && !s._offL);
    this._sysResize = S.filter(s => s.resize);
  }

  register(system) {
    if (!system.name) throw new Error('system needs a name');
    if (this._byName.has(system.name)) throw new Error('duplicate system ' + system.name);
    system.order = system.order ?? 50;
    this.systems.push(system);
    this._byName.set(system.name, system);
    this.systems.sort((a, b) => a.order - b.order);
    return system;
  }
  get(name) { return this._byName.get(name); }

  async initSystems(world) {
    for (const s of this.systems) if (s.init) await s.init(world);
    this._refreshLists();
    this.resize();
  }

  _bindResize() {
    this._onResize = () => this.resize();
    window.addEventListener('resize', this._onResize, { passive: true });
    if (typeof ResizeObserver !== 'undefined') {
      this._resizeObs = new ResizeObserver(this._onResize);
      this._resizeObs.observe(this.canvas.parentElement || this.canvas);
    }
  }

  resize() {
    const el = this.canvas;
    const w = Math.max(2, el.clientWidth || window.innerWidth);
    const h = Math.max(2, el.clientHeight || window.innerHeight);
    const dpr = clamp(window.devicePixelRatio || 1, 0.5, CFG.q.dpr);
    if (w === this.size.w && h === this.size.h && dpr === this.size.dpr) return;
    this.size = { w, h, dpr };
    this.renderer.setPixelRatio(dpr);
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    if (this.pipeline?.setSize) this.pipeline.setSize(w, h, dpr);
    for (const s of this._sysResize || []) s.resize(w, h, dpr);
    this.bus.emit('resize', w, h, dpr);
  }

  start(world) {
    this.world = world;
    if (this.running) return;
    this.running = true;
    this._last = performance.now();
    const tick = (now) => {
      this._raf = requestAnimationFrame(tick);
      this.frame(now);
    };
    this._raf = requestAnimationFrame(tick);
  }
  stop() { this.running = false; cancelAnimationFrame(this._raf); }

  /** One real frame driven by rAF. */
  frame(now) {
    const t = this.time;
    let dt = (now - this._last) / 1000;
    this._last = now;
    if (!(dt > 0)) dt = 1 / 60;
    dt = Math.min(dt, CFG.maxFrameDt);
    this.advance(dt);
  }

  /**
   * Advance the world by `realDt` seconds of wall time and render one frame.
   * Deterministic when called with a fixed realDt (used by the capture harness).
   *
   * `skipRender` runs the identical simulation and skips ONLY the draw. It
   * exists because a capture scenario spends almost all of its time travelling
   * to the moment it wants to photograph: advancing 20 s at dt 1/10 pays for
   * 200 software-rasterised frames to produce one picture, which is why a
   * four-scenario capture takes half an hour on this box. Everything above the
   * draw call still runs, so the sim, the camera solve, the VFX and the RNG
   * stream are bit-identical either way.
   *
   * It is NOT free of consequence for the image: TAA history, motion vectors
   * and any other frame-to-frame feedback in the post chain only accumulate on
   * frames that actually draw. So never skip the frames immediately before a
   * shot — `__DW.warp()` renders a tail for exactly this reason — and never
   * use it to reach a frame whose look you are about to judge without one.
   */
  advance(realDt, skipRender = false) {
    const t = this.time, w = this.world;
    // Nothing can be drawn and nothing should advance while the GPU context is
    // gone: stepping the simulation against a dead renderer only banks time the
    // player never saw. frame() has already moved `_last`, so no dt accumulates.
    if (this.contextLost) return;
    const cpu0 = performance.now();
    t.unscaledDt = realDt;
    t.real += realDt;

    // Hitstop consumes real time but freezes the simulation.
    let scaled = realDt;
    if (t.hitstop > 0) {
      const used = Math.min(t.hitstop, realDt);
      t.hitstop -= used;
      scaled = (realDt - used) * t.slowmo;
    } else {
      scaled = realDt * t.slowmo;
    }
    t.scale = realDt > 0 ? scaled / realDt : 1;
    t.dt = scaled;
    t.elapsed += scaled;

    this._acc += scaled;
    const fdt = t.fixedDt;
    let steps = 0;
    while (this._acc >= fdt && steps < 6) {
      t.sim += fdt; t.tick++;
      for (let i = 0; i < this._sysFixed.length; i++) {
        const sy = this._sysFixed[i];
        try { sy.fixedUpdate(w, fdt); if (sy._failF) sy._failF = 0; }
        catch (err) { this._fault(sy, 'fixedUpdate', '_failF', '_offF', err); }
      }
      this._acc -= fdt; steps++;
    }
    if (steps >= 6) this._acc = 0; // spiral-of-death guard
    t.alpha = this._acc / fdt;

    for (let i = 0; i < this._sysUpdate.length; i++) {
      const sy = this._sysUpdate[i];
      try { sy.update(w, t.dt, t.alpha, realDt); if (sy._failU) sy._failU = 0; }
      catch (err) { this._fault(sy, 'update', '_failU', '_offU', err); }
    }
    for (let i = 0; i < this._sysLate.length; i++) {
      const sy = this._sysLate[i];
      try { sy.lateUpdate(w, t.dt, t.alpha, realDt); if (sy._failL) sy._failL = 0; }
      catch (err) { this._fault(sy, 'lateUpdate', '_failL', '_offL', err); }
    }

    // Skipped draw: keep the frame counter and the CPU timing honest, but do
    // not touch drawCalls/triangles -- they still describe the last frame
    // actually drawn, which is what a capture's stats.json should report.
    if (skipRender) { t.frame++; this.perf.cpuMs = performance.now() - cpu0; return; }

    this.renderer.info.reset();
    if (this.pipeline) this.pipeline.render(this.scene, this.camera, t.dt, realDt);
    else this.renderer.render(this.scene, this.camera);

    t.frame++;
    const p = this.perf, info = this.renderer.info;
    p.drawCalls = info.render.calls; p.triangles = info.render.triangles;
    p.geometries = info.memory.geometries; p.textures = info.memory.textures;
    p.programs = this.renderer.info.programs?.length || 0;
    p.cpuMs = performance.now() - cpu0;
    const ms = realDt * 1000;
    p.samples[p.si = (p.si + 1) % p.samples.length] = ms;
    let sum = 0; for (let i = 0; i < p.samples.length; i++) sum += p.samples[i] || ms;
    p.avg = sum / p.samples.length; p.ms = ms; p.fps = 1000 / Math.max(0.01, p.avg);
  }

  /** Freeze the sim for `s` seconds of real time (impact punch). Takes the max. */
  hitstop(s) { this.time.hitstop = Math.max(this.time.hitstop, s); }

  /**
   * Start a new deterministic battle clock without rewinding presentation time.
   * `real` and `frame` stay monotonic because audio retrigger throttles and TAA
   * history use them; gameplay decisions use `sim` / `tick`, which must match a
   * fresh boot after retry. Any in-flight hitstop or slow-motion is cancelled.
   */
  resetBattleTime() {
    const t = this.time;
    t.elapsed = 0; t.sim = 0; t.dt = 0;
    t.tick = 0; t.alpha = 0; t.scale = 1; t.unscaledDt = 0;
    t.hitstop = 0; t.slowmo = 1;
    this._acc = 0;
    if (this.running) this._last = performance.now();
    return t;
  }

  dispose() {
    this.stop();
    window.removeEventListener('resize', this._onResize);
    if (this._onCtxLost) this.canvas.removeEventListener('webglcontextlost', this._onCtxLost);
    if (this._onCtxRestored) this.canvas.removeEventListener('webglcontextrestored', this._onCtxRestored);
    this._resizeObs?.disconnect();
    for (const s of this.systems) s.dispose?.();
    this.renderer.dispose();
  }
}
